import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    Box, Typography, TextField, Button, Grid, IconButton,
    Card, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, InputAdornment, Dialog,
    DialogContent, DialogActions, Avatar, Autocomplete, Paper, Tooltip,
    CircularProgress, Alert,
} from '@mui/material';
import http, { apiErrorMessage } from '../../../Api/http';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineRounded';
import DownloadIcon from '@mui/icons-material/Download';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SnackBar from '../../SnackBar';
import {
    getEmployees, postSalaryStructure, salaryStructureDashboard,
    updateSalaryStructureByEmployeeCode, deleteSalaryStructureByEmployeeCode,
    GetSalarySecurityStatus, PostSalaryResetViaRecovery,
} from '../../../Api/Api';
import { SkeletonRows, solidBtn, tonalBtn, field } from '../../uiKit';
import SecretDialog from '../../SecretDialog';
import { useCrumbActionsSetter } from '../../../utils/crumbActions';
import { selectModules } from '../../../redux/slices/authSlice';
import { hasModule } from '../../../data/serverModules';
import {
    useSalaryPassphrase, setSalaryPassphrase, clearSalaryPassphrase, passphraseConfig,
} from '../../../utils/salaryPassphrase';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_DARK = '#6246E0';
const PRIMARY_BORDER = '#C9BEFB';

const AVATAR_PALETTE = ['#0E7490', '#1D4ED8', '#C2410C', '#6246E0', '#1D4ED8', '#BE185D', '#A16207', '#0F766E'];
const avatarColorFor = (name = '') => {
    const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};
const getInitials = (name = '') => name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const EMPTY_FORM = { employeeCode: '', basicSalary: '', hra: 40, da: 10, conveyance: 1600, specialAllowance: 0 };

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '7px', fontSize: '13px', bgcolor: '#F9FAFB',
        '&:hover': { bgcolor: '#fff' }, '&.Mui-focused': { bgcolor: '#fff' },
        '& fieldset': { borderColor: '#E5E7EB' },
        '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};
const ghostSx = {
    textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#374151', borderRadius: '7px',
    border: '1px solid #E5E7EB', px: 2, height: 38, '&:hover': { bgcolor: '#F9FAFB' },
};

// Amounts for one row: HRA / DA are percentages of basic.
export const figures = (s) => {
    const basic = Number(s.basicSalary) || 0;
    const hraP = s.hraPercent ?? s.hra ?? 0;
    const daP = s.daPercent ?? s.da ?? 0;
    const conveyance = Number(s.conveyanceAllowance ?? s.conveyance) || 0;
    const special = Number(s.specialAllowance) || 0;
    const hraAmt = Math.round(basic * hraP / 100);
    const daAmt = Math.round(basic * daP / 100);
    const gross = s.totalEarnings ? Number(s.totalEarnings) : basic + hraAmt + daAmt + conveyance + special;
    return { basic, hraP, daP, conveyance, special, hraAmt, daAmt, gross };
};

// What went wrong, in words the user can act on.
const explain = (err, fallback) => {
    const d = err?.response?.data;
    if (err?.response?.status === 423) {
        const until = d?.lockedUntil ? new Date(d.lockedUntil) : null;
        const when = until && !Number.isNaN(until.getTime()) ? ` Try again after ${until.toLocaleString()}.` : '';
        return `Too many incorrect attempts.${when}`;
    }
    if (err?.response?.status === 401) return 'Incorrect password.';
    return apiErrorMessage(err, fallback);
};

export default function SalaryStructures() {
    const navigate = useNavigate();
    const modules = useSelector(selectModules);
    const canUnlock = hasModule(modules, 'salary-unlock');
    const passphrase = useSalaryPassphrase();

    const [structures, setStructures] = useState([]);
    const [kpiData, setKpiData] = useState({ totalStructures: 0, totalEmployees: 0 });
    const [amountsLocked, setAmountsLocked] = useState(false);
    const [status, setStatus] = useState(null);
    const [statusIssue, setStatusIssue] = useState('');
    const [employeesData, setEmployeesData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterText, setFilterText] = useState('');

    const [snack, setSnack] = useState({ open: false, ok: false, msg: '' });
    const showSnack = (msg, ok) => setSnack({ open: true, ok, msg });

    // structure dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [structureToDelete, setStructureToDelete] = useState(null);

    // security dialogs: 'unlock' | 'forgot' | null (setup / change / rotate live on the Salary Security page)
    const [panel, setPanel] = useState(null);
    const [panelBusy, setPanelBusy] = useState(false);
    const [panelError, setPanelError] = useState('');
    const afterUnlock = useRef(null);

    const setupDone = status?.setupCompleted === true;
    const needsPassphrase = setupDone && !passphrase;
    // Once encryption is set up, the KPIs and table stay hidden until the passphrase is entered (again after every refresh).
    const hideData = needsPassphrase && canUnlock && !isLoading;
    const lockedUntil = status?.pinLockedUntil && new Date(status.pinLockedUntil) > new Date() ? new Date(status.pinLockedUntil) : null;

    const openPanel = (name) => { setPanelError(''); setPanel(name); };
    const closePanel = () => { if (!panelBusy) { setPanel(null); afterUnlock.current = null; } };

    const loadStatus = useCallback(async () => {
        if (!canUnlock) { setStatus(null); setStatusIssue('noaccess'); return null; }
        try {
            const res = await http.get(GetSalarySecurityStatus);
            const d = res.data?.data || null;
            setStatus(d);
            setStatusIssue('');
            return d;
        } catch (err) {
            setStatus(null);
            setStatusIssue(err?.response?.status === 403 ? 'noaccess' : 'failed');
            return null;
        }
    }, [canUnlock]);

    // Pass `pass` to try a passphrase before keeping it; returns the dashboard payload (or null on failure).
    const loadDashboard = useCallback(async ({ silent = false, pass } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const res = await http.get(salaryStructureDashboard, passphraseConfig(pass));
            if (res.data && !res.data.error) {
                const d = res.data.data || {};
                setKpiData({ totalStructures: d.totalStructures || 0, totalEmployees: d.totalEmployees || 0 });
                setAmountsLocked(!!d.amountsLocked);
                setStructures((d.salaryStructures || []).map((s) => ({ ...s, rollNumber: s.employeeCode ?? s.rollNumber ?? '' })));
                return d;
            }
            showSnack(res.data?.message || 'Failed to load salary structures', false);
        } catch (err) {
            // No structure yet comes back as 404 — an empty list, not a failure.
            if (err?.response?.status === 404) {
                setStructures([]); setKpiData((k) => ({ ...k, totalStructures: 0 })); setAmountsLocked(false);
            } else showSnack(explain(err, 'Failed to load salary structures'), false);
        } finally {
            if (!silent) setIsLoading(false);
        }
        return null;
    }, []);

    useEffect(() => {
        (async () => {
            http.get(getEmployees).then((res) => {
                const list = Array.isArray(res.data?.data) ? res.data.data : [];
                setEmployeesData(list.map((e) => ({ ...e, rollNumber: e.employeeCode ?? e.rollNumber ?? '' })));
            }).catch(() => setEmployeesData([]));
            await loadStatus();
            loadDashboard();
        })();
        // The passphrase is only ever held while this screen is open.
        return () => clearSalaryPassphrase();
    }, [loadStatus, loadDashboard]);

    // ---- unlock / security flows
    const requestUnlock = (then) => {
        if (!canUnlock) { showSnack('Your role is not allowed to unlock salary figures.', false); return; }
        if (!setupDone) { navigate('/dashboard/payroll-setup?tab=security'); return; }
        afterUnlock.current = then || null;
        openPanel('unlock');
    };

    const submitUnlock = async ({ pass }) => {
        setPanelBusy(true); setPanelError('');
        try {
            const d = await loadDashboard({ silent: true, pass });
            if (!d) { setPanelError('Could not verify the password. Try again.'); return; }
            if (d.amountsLocked) { setPanelError('Incorrect password.'); loadStatus(); return; }
            setSalaryPassphrase(pass);
            const next = afterUnlock.current;
            afterUnlock.current = null;
            setPanel(null);
            if (next) setTimeout(next, 0);
        } finally { setPanelBusy(false); }
    };

    const submitForgot = async ({ key, pass, confirm }) => {
        if (pass.length < 8) { setPanelError('Use at least 8 characters.'); return; }
        if (pass !== confirm) { setPanelError('The two passwords do not match.'); return; }
        setPanelBusy(true); setPanelError('');
        try {
            await http.post(PostSalaryResetViaRecovery, { newPassphrase: pass }, { headers: { 'X-Recovery-Key': key.trim() } });
            setSalaryPassphrase(pass);
            setPanel(null);
            showSnack('Password reset. Your recovery key still works.', true);
            loadDashboard({ silent: true, pass });
        } catch (err) {
            setPanelError(err?.response?.status === 401 ? 'Incorrect recovery key.' : explain(err, 'Could not reset the password.'));
            loadStatus();
        } finally { setPanelBusy(false); }
    };

    const lockNow = () => { clearSalaryPassphrase(); loadDashboard({ silent: true }); };

    // ---- structure create / edit / delete
    const openStructureDialog = (structure = null) => {
        if (needsPassphrase) { requestUnlock(() => openStructureDialog(structure)); return; }
        if (structure && structure.amountsLocked) { requestUnlock(() => openStructureDialog(structure)); return; }
        if (structure) {
            setEditMode(true);
            const f = figures(structure);
            setForm({
                employeeCode: structure.employeeCode || structure.rollNumber || '',
                basicSalary: f.basic, hra: f.hraP, da: f.daP, conveyance: f.conveyance, specialAllowance: f.special,
            });
            setSelectedEmp(employeesData.find((e) => e.rollNumber === (structure.employeeCode || structure.rollNumber)) || { rollNumber: structure.employeeCode, name: structure.name });
        } else {
            setEditMode(false); setForm(EMPTY_FORM); setSelectedEmp(null);
        }
        setOpenDialog(true);
    };
    const closeStructureDialog = () => { setOpenDialog(false); setEditMode(false); setSelectedEmp(null); };

    const calculateTotal = () => {
        const basic = Number(form.basicSalary) || 0;
        return basic + Math.round(basic * (Number(form.hra) || 0) / 100) + Math.round(basic * (Number(form.da) || 0) / 100)
            + (Number(form.conveyance) || 0) + (Number(form.specialAllowance) || 0);
    };

    const handleSave = async () => {
        if (!form.employeeCode || !form.basicSalary) { showSnack('Please select an employee and enter basic salary', false); return; }
        const body = {
            EmployeeCode: form.employeeCode,
            BasicSalary: Number(form.basicSalary),
            HraPercent: Number(form.hra),
            DaPercent: Number(form.da),
            ConveyanceAllowance: String(form.conveyance),
            SpecialAllowance: String(form.specialAllowance),
            TotalEarnings: calculateTotal(),
        };
        setIsSaving(true);
        try {
            const cfg = passphraseConfig();
            const res = editMode
                ? await http.put(updateSalaryStructureByEmployeeCode, body, cfg)
                : await http.post(postSalaryStructure, body, cfg);
            if (res.data && !res.data.error) {
                await loadDashboard({ silent: true });
                showSnack(editMode ? 'Salary structure updated successfully!' : 'Salary structure created successfully!', true);
                closeStructureDialog();
            } else showSnack(res.data?.message || 'Could not save the salary structure', false);
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 423) clearSalaryPassphrase();
            showSnack(explain(err, 'Could not save the salary structure.'), false);
            loadStatus();
        } finally { setIsSaving(false); }
    };

    const handleDelete = async (structure) => {
        const empCode = structure.employeeCode || structure.rollNumber;
        if (!empCode) { showSnack('Cannot delete: employee code not found', false); return; }
        try {
            const res = await http.delete(deleteSalaryStructureByEmployeeCode, { params: { employeeCode: empCode } });
            if (res.data && !res.data.error) {
                showSnack('Salary structure deleted successfully!', true);
                loadDashboard({ silent: true });
            } else showSnack(res.data?.message || 'Failed to delete salary structure', false);
        } catch (err) { showSnack(apiErrorMessage(err, 'Failed to delete salary structure.'), false); }
    };

    // ---- derived table data
    const filtered = useMemo(() => {
        const q = filterText.toLowerCase().trim();
        if (!q) return structures;
        return structures.filter((s) => (s.name || '').toLowerCase().includes(q) || (s.rollNumber || '').toLowerCase().includes(q));
    }, [structures, filterText]);

    const sumGross = (list) => list.reduce((t, s) => t + (s.amountsLocked ? 0 : figures(s).gross), 0);
    const totals = useMemo(() => {
        const grossSum = sumGross(structures);
        return { grossSum, avgGross: structures.length ? Math.round(grossSum / structures.length) : 0 };
    }, [structures]);
    const visibleGross = useMemo(() => sumGross(filtered), [filtered]);

    const handleExport = () => {
        if (needsPassphrase || amountsLocked) { requestUnlock(handleExport); return; }
        if (!filtered.length) { showSnack('No salary structures to export.', false); return; }
        const rows = filtered.map((s, i) => {
            const f = figures(s);
            return {
                'S.No': i + 1, 'Employee Code': s.rollNumber || '', 'Employee Name': s.name || '',
                'Designation': s.designation || '', 'Department': s.department || '',
                'Basic Salary': f.basic, 'HRA %': f.hraP, 'HRA Amount': f.hraAmt, 'DA %': f.daP, 'DA Amount': f.daAmt,
                'Conveyance': f.conveyance, 'Special Allowance': f.special, 'Gross Salary': f.gross,
            };
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Salary Structures');
        XLSX.writeFile(wb, `Salary_Structures_${new Date().toISOString().split('T')[0]}.xlsx`);
        showSnack(`Exported ${filtered.length} salary structure${filtered.length === 1 ? '' : 's'}.`, true);
    };

    const coverage = kpiData.totalEmployees > 0 ? Math.round((kpiData.totalStructures / kpiData.totalEmployees) * 100) : 0;
    const mask = (v) => (amountsLocked ? '••••' : v);
    const kpiCards = [
        { label: 'Total Structures', value: kpiData.totalStructures, sub: `/ ${kpiData.totalEmployees} staff`, color: '#7C5CFC', bg: '#F1EEFE', icon: AssignmentIcon },
        { label: 'Covered Staff', value: `${coverage}%`, sub: `${kpiData.totalStructures} of ${kpiData.totalEmployees}`, color: '#16A34A', bg: '#DCFCE7', icon: PeopleAltOutlinedIcon },
        { label: 'Avg. Gross Salary', value: mask(formatINR(totals.avgGross)), sub: 'per structure', color: '#0EA5E9', bg: '#E0F2FE', icon: TrendingUpIcon },
        { label: 'Monthly Payout', value: mask(formatINR(totals.grossSum)), sub: 'all structures', color: '#F59E0B', bg: '#FFF7ED', icon: PaidOutlinedIcon },
    ];

    const searchActive = filterText.trim().length > 0;
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { handleExport, openStructureDialog };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small" placeholder="Search by name or roll number..." value={filterText} onChange={(e) => setFilterText(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: searchActive ? PRIMARY : '#9CA3AF' }} /></InputAdornment>,
                            endAdornment: searchActive ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setFilterText('')} sx={{ p: 0.3 }}><CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} /></IconButton>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    sx={{ ...field, width: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 42, bgcolor: '#fff' } }}
                />
                <Button startIcon={<DownloadIcon sx={{ fontSize: 18 }} />} onClick={() => actionsRef.current.handleExport()} sx={{ ...tonalBtn, height: 42, px: 2 }}>Export</Button>
                <Button startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />} onClick={() => actionsRef.current.openStructureDialog()} sx={{ ...solidBtn, height: 42, px: 2.2 }}>Create Structure</Button>
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, filterText, searchActive]);

    const lockButton = (
        <Tooltip arrow title={canUnlock ? 'Enter password to view' : 'Your role cannot unlock salary figures'}>
            <span>
                <IconButton size="small" disabled={!canUnlock} onClick={() => requestUnlock()} sx={{ p: 0.4 }}>
                    <LockOutlinedIcon sx={{ fontSize: 16, color: canUnlock ? PRIMARY : '#9CA3AF' }} />
                </IconButton>
            </span>
        </Tooltip>
    );

    const available = employeesData.filter((e) => !structures.some((s) => s.rollNumber === e.rollNumber));

    return (
        <>
            <SnackBar open={snack.open} color={snack.ok} setOpen={(o) => setSnack((s) => ({ ...s, open: o }))} status={snack.ok} message={snack.msg} />

            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '88vh', px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
                <Box sx={{ flex: 1 }}>

                    {statusIssue === 'noaccess' && (
                        <Alert severity="info" icon={<SecurityOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                            Salary encryption setup is hidden because this login does not have the "Unlock Salary Figures" permission.
                            Enable it under Roles &amp; Access (Payroll Setup), then sign out and back in.
                        </Alert>
                    )}
                    {statusIssue === 'failed' && (
                        <Alert
                            severity="warning" icon={<SecurityOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                            action={<Button size="small" onClick={loadStatus} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}
                        >
                            Could not check the salary encryption status. The server may not have this feature deployed yet.
                        </Alert>
                    )}
                    {canUnlock && status && !setupDone && (
                        <Alert
                            severity="info" icon={<SecurityOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                            action={<Button size="small" onClick={() => navigate('/dashboard/payroll-setup?tab=security')} sx={{ textTransform: 'none', fontWeight: 700, color: PRIMARY_DARK }}>Set up</Button>}
                        >
                            Salary figures are not encrypted yet. Set up salary encryption to protect them with an Encryption Password.
                        </Alert>
                    )}
                    {needsPassphrase && !hideData && (
                        <Alert
                            severity="warning" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                            action={<Button size="small" onClick={() => requestUnlock()} sx={{ textTransform: 'none', fontWeight: 700, color: '#92400E' }}>Unlock</Button>}
                        >
                            Enter the password to view salary figures.{lockedUntil ? ` Locked until ${lockedUntil.toLocaleString()}.` : ''}
                        </Alert>
                    )}
                    {!canUnlock && amountsLocked && (
                        <Alert severity="warning" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                            Your role can see the list but is not allowed to unlock salary figures.
                        </Alert>
                    )}
                    {setupDone && passphrase && (
                        <Alert
                            severity="success" icon={<LockOpenOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                            action={<Button size="small" onClick={lockNow} sx={{ textTransform: 'none', fontWeight: 700 }}>Lock</Button>}
                        >
                            Salary figures are unlocked for this screen.
                        </Alert>
                    )}

                    {hideData ? (
                        <Card sx={{ border: '1px solid #ECEBF5', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)', bgcolor: '#fff', py: 7, textAlign: 'center' }}>
                            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                                <LockOutlinedIcon sx={{ fontSize: 26, color: PRIMARY }} />
                            </Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Salary data is protected</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.5, mb: 2 }}>Enter the Encryption Password to view salary structures.</Typography>
                            <Button onClick={() => requestUnlock()} startIcon={<LockOpenOutlinedIcon sx={{ fontSize: 18 }} />} sx={{ ...solidBtn, height: 40, px: 2.5 }}>Unlock</Button>
                        </Card>
                    ) : (<>
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                        {kpiCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                                    <Box sx={{
                                        p: 2.5, borderRadius: '7px', bgcolor: card.bg, border: `1px solid ${card.color}22`,
                                        boxShadow: '0 1px 3px rgba(16,24,40,0.06)', height: '100%',
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: '11px', color: card.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.label}</Typography>
                                                <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#0F172A', lineHeight: 1.2, mt: 0.5 }} noWrap>{card.value}</Typography>
                                                <Typography sx={{ fontSize: '10.5px', color: '#6B7280', fontWeight: 600, mt: 0.4 }} noWrap>{card.sub}</Typography>
                                            </Box>
                                            <Box sx={{ width: 44, height: 44, borderRadius: '7px', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 1 }}>
                                                <Icon sx={{ color: card.color, fontSize: 22 }} />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Card sx={{ border: '1px solid #ECEBF5', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)', bgcolor: '#fff', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {['S.No', 'Employee', 'Roll No.', 'Basic', 'HRA', 'DA', 'Conveyance', 'Special', 'Gross Salary', 'Actions'].map((header) => (
                                            <TableCell key={header} sx={{ fontWeight: 700, fontSize: '10.5px', color: '#6E6B99', bgcolor: '#F4F3FB', textTransform: 'uppercase', letterSpacing: 0.7, whiteSpace: 'nowrap', py: 1.5, borderBottom: '1px solid #E8E6F3' }}>
                                                {header}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, borderBottom: 'none' }}><SkeletonRows count={5} p={0} /></TableCell></TableRow>
                                    ) : filtered.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                                <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#F3F4F6', mx: 'auto', mb: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <AssignmentIcon sx={{ fontSize: 28, color: '#9CA3AF' }} />
                                                </Box>
                                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                                    {searchActive ? `No results for "${filterText}"` : 'No salary structures created yet'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', mt: 0.4 }}>
                                                    {searchActive ? 'Try a different search term' : 'Click "Create Structure" to add the first one'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : filtered.map((s, idx) => {
                                        const locked = !!s.amountsLocked;
                                        const f = figures(s);
                                        const avColor = avatarColorFor(s.name || '');
                                        const cellSx = { borderBottom: '1px solid #EEF0F6', py: 1.4 };
                                        const num = { fontSize: '12.5px', color: '#374151', fontWeight: 600 };
                                        return (
                                            <TableRow key={s.id ?? s.rollNumber} sx={{ bgcolor: idx % 2 ? '#FBFAFE' : '#fff', '&:hover': { bgcolor: '#F5F4FC' }, transition: 'background-color 0.15s' }}>
                                                <TableCell sx={{ ...cellSx, width: 50 }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>{idx + 1}</Typography></TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: `${avColor}15`, color: avColor, fontSize: '11px', fontWeight: 700, border: `1px solid ${avColor}33` }}>{getInitials(s.name || '?')}</Avatar>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>{s.name || '—'}</Typography>
                                                            <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500 }}>{s.designation || s.department || 'Staff Member'}</Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={cellSx}><Chip label={s.rollNumber || '—'} size="small" sx={{ bgcolor: '#EEF1F6', color: '#475569', fontWeight: 600, fontSize: '10.5px', height: 22, border: '1px solid #E2E7EF' }} /></TableCell>
                                                <TableCell sx={cellSx}><Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', display: 'inline-flex' }}>{locked ? lockButton : formatINR(f.basic)}</Typography></TableCell>
                                                <TableCell sx={cellSx}>
                                                    {locked ? '••••' : (<>
                                                        <Chip label={`${f.hraP}%`} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', border: '1px solid #DDE0FB', fontWeight: 700, fontSize: '10.5px', height: 20 }} />
                                                        <Typography sx={{ fontSize: '10.5px', color: '#6B7280', mt: 0.3, fontWeight: 600 }}>{formatINR(f.hraAmt)}</Typography>
                                                    </>)}
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    {locked ? '••••' : (<>
                                                        <Chip label={`${f.daP}%`} size="small" sx={{ bgcolor: '#F1EEFE', color: '#6D28D9', border: '1px solid #E4DBFB', fontWeight: 700, fontSize: '10.5px', height: 20 }} />
                                                        <Typography sx={{ fontSize: '10.5px', color: '#6B7280', mt: 0.3, fontWeight: 600 }}>{formatINR(f.daAmt)}</Typography>
                                                    </>)}
                                                </TableCell>
                                                <TableCell sx={cellSx}><Typography sx={num}>{locked ? '••••' : formatINR(f.conveyance)}</Typography></TableCell>
                                                <TableCell sx={cellSx}><Typography sx={num}>{locked ? '••••' : formatINR(f.special)}</Typography></TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.1, py: 0.5, borderRadius: '7px', bgcolor: '#EFECFE', border: '1px solid #DDD3FB' }}>
                                                        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 800, color: '#5B21B6', display: 'inline-flex' }}>{locked ? lockButton : formatINR(f.gross)}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip arrow title={locked ? 'Unlock to edit' : 'Edit structure'}>
                                                            <IconButton size="small" onClick={() => openStructureDialog(s)} sx={{ bgcolor: '#F3F0FE', borderRadius: '7px', border: '1px solid #C9BEFB', '&:hover': { bgcolor: '#E7DFFC' } }}>
                                                                {locked ? <LockOutlinedIcon sx={{ fontSize: 14, color: '#6246E0' }} /> : <EditIcon sx={{ fontSize: 14, color: '#6246E0' }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip arrow title="Delete structure">
                                                            <IconButton size="small" onClick={() => setStructureToDelete(s)} sx={{ bgcolor: '#FEF2F2', borderRadius: '7px', border: '1px solid #FECACA', '&:hover': { bgcolor: '#FEE2E2' } }}>
                                                                <DeleteIcon sx={{ fontSize: 14, color: '#DC2626' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!isLoading && filtered.length > 0 && (
                            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #EAE7F7', bgcolor: '#F7F6FD', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                    Showing <Box component="span" sx={{ color: PRIMARY_DARK, fontWeight: 800 }}>{filtered.length}</Box> of {structures.length} structures
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                                    <Typography sx={{ fontSize: '11px', color: PRIMARY_DARK, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Total Gross</Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 800, color: PRIMARY_DARK }}>{mask(formatINR(visibleGross))}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Card>
                    </>)}
                </Box>
            </Box>

            {/* Security menu */}
            <SecretDialog
                open={panel === 'unlock'} title="Unlock salary figures" subtitle="Enter the Encryption Password for this entity."
                fields={[{ key: 'pass', label: 'Encryption Password' }]} submitLabel="Unlock" busy={panelBusy} error={panelError}
                onClose={closePanel} onSubmit={submitUnlock}
            >
                <Button size="small" onClick={() => openPanel('forgot')} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: PRIMARY_DARK, fontWeight: 600, p: 0 }}>Forgot password?</Button>
            </SecretDialog>

            <SecretDialog
                open={panel === 'forgot'} title="Forgot password" subtitle="Use the 24-character recovery key issued at setup. No salary data is lost."
                fields={[{ key: 'key', label: 'Recovery key' }, { key: 'pass', label: 'New password', helper: 'At least 8 characters' }, { key: 'confirm', label: 'Confirm new password' }]}
                submitLabel="Reset password" busy={panelBusy} error={panelError} onClose={closePanel} onSubmit={submitForgot}
            />

            {/* Create / edit structure */}
            <Dialog open={openDialog} onClose={closeStructureDialog} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: '7px', overflow: 'hidden' } } }}>
                <Box sx={{ px: 2.5, py: 2, background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`, borderBottom: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '7px', bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {editMode ? <EditIcon sx={{ color: PRIMARY, fontSize: 20 }} /> : <AddCircleOutlineIcon sx={{ color: PRIMARY, fontSize: 20 }} />}
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{editMode ? 'Edit Salary Structure' : 'Create New Salary Structure'}</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: '#6B7280', mt: 0.3 }}>Define basic pay, allowances and gross salary for a staff member</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={closeStructureDialog} sx={{ width: 32, height: 32, borderRadius: '7px', bgcolor: '#fff', border: '1px solid #E5E7EB' }}><CloseIcon sx={{ fontSize: 16, color: '#6B7280' }} /></IconButton>
                </Box>

                <DialogContent sx={{ p: 2.5, bgcolor: '#F9FAFB' }}>
                    <Box sx={{ p: 2, mb: 2, bgcolor: '#fff', borderRadius: '7px', border: '1px solid #E5E7EB' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <PersonOutlineIcon sx={{ fontSize: 16, color: '#6246E0' }} />
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.6 }}>Employee</Typography>
                        </Box>
                        <Autocomplete
                            options={editMode ? employeesData : available}
                            getOptionLabel={(o) => `${o.rollNumber} — ${o.name}`}
                            value={selectedEmp} disabled={editMode}
                            onChange={(_, v) => { setSelectedEmp(v); setForm((f) => ({ ...f, employeeCode: v ? v.rollNumber : '' })); }}
                            isOptionEqualToValue={(o, v) => o.rollNumber === v.rollNumber}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Employee *" size="small" slotProps={{ ...params.slotProps, inputLabel: { ...params.slotProps?.inputLabel, shrink: true } }} sx={fieldSx} />
                            )}
                        />
                        {editMode && <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', mt: 0.6, fontStyle: 'italic' }}>Employee cannot be changed when editing an existing structure.</Typography>}
                    </Box>

                    <Box sx={{ p: 2, mb: 2, bgcolor: '#fff', borderRadius: '7px', border: '1px solid #E5E7EB' }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.5 }}>Salary Components</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Basic Salary *" type="number" size="small" fullWidth value={form.basicSalary}
                                    onChange={(e) => setForm((f) => ({ ...f, basicSalary: e.target.value }))}
                                    slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }} sx={fieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Conveyance Allowance" type="number" size="small" fullWidth value={form.conveyance}
                                    onChange={(e) => setForm((f) => ({ ...f, conveyance: e.target.value }))}
                                    slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }} sx={fieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="HRA (% of Basic)" type="number" size="small" fullWidth value={form.hra}
                                    onChange={(e) => setForm((f) => ({ ...f, hra: e.target.value }))}
                                    helperText={`= ${formatINR(Math.round((Number(form.basicSalary) || 0) * (Number(form.hra) || 0) / 100))}`}
                                    slotProps={{ inputLabel: { shrink: true }, input: { endAdornment: <InputAdornment position="end">%</InputAdornment> }, formHelperText: { sx: { fontSize: '10.5px', fontWeight: 600, color: '#6246E0' } } }} sx={fieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="DA (% of Basic)" type="number" size="small" fullWidth value={form.da}
                                    onChange={(e) => setForm((f) => ({ ...f, da: e.target.value }))}
                                    helperText={`= ${formatINR(Math.round((Number(form.basicSalary) || 0) * (Number(form.da) || 0) / 100))}`}
                                    slotProps={{ inputLabel: { shrink: true }, input: { endAdornment: <InputAdornment position="end">%</InputAdornment> }, formHelperText: { sx: { fontSize: '10.5px', fontWeight: 600, color: '#6246E0' } } }} sx={fieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Special Allowance" type="number" size="small" fullWidth value={form.specialAllowance}
                                    onChange={(e) => setForm((f) => ({ ...f, specialAllowance: e.target.value }))}
                                    slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }} sx={fieldSx}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Paper elevation={0} sx={{ p: 2, borderRadius: '7px', border: `1px solid ${PRIMARY_BORDER}`, background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: PRIMARY_DARK, textTransform: 'uppercase', letterSpacing: 0.6 }}>Estimated Gross Earnings</Typography>
                                <Typography sx={{ fontSize: '21px', fontWeight: 800, color: PRIMARY_DARK, lineHeight: 1.1, mt: 0.4 }}>{formatINR(calculateTotal())}</Typography>
                                <Typography sx={{ fontSize: '10.5px', color: '#4B5563', mt: 0.4 }}>Basic + HRA + DA + Conveyance + Special Allowance</Typography>
                            </Box>
                            <AccountBalanceWalletIcon sx={{ fontSize: 26, color: PRIMARY }} />
                        </Box>
                    </Paper>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, py: 1.8, borderTop: '1px solid #E5E7EB', bgcolor: '#fff' }}>
                    <Button onClick={closeStructureDialog} sx={ghostSx}>Cancel</Button>
                    <Button
                        startIcon={isSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveOutlinedIcon sx={{ fontSize: 18 }} />}
                        onClick={handleSave} disabled={isSaving}
                        sx={{ ...solidBtn, px: 2.5, height: 38, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}
                    >
                        {isSaving ? 'Saving…' : editMode ? 'Update Structure' : 'Create Structure'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!structureToDelete} onClose={() => setStructureToDelete(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '7px', overflow: 'hidden' } } }}>
                <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#FEF2F2', border: '4px solid #FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.8 }}>
                        <WarningAmberIcon sx={{ fontSize: 32, color: '#DC2626' }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: '#111827', mb: 0.8 }}>Delete Salary Structure?</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#6B7280', px: 1.5, lineHeight: 1.5 }}>
                        Are you sure you want to delete the salary structure for <strong style={{ color: '#111827' }}>{structureToDelete?.name || 'this employee'}</strong>? Payroll cannot run for them until a new one is created.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                    <Button fullWidth onClick={() => setStructureToDelete(null)} sx={ghostSx}>Cancel</Button>
                    <Button
                        fullWidth variant="contained" startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                        onClick={() => { const s = structureToDelete; setStructureToDelete(null); handleDelete(s); }}
                        sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 700, borderRadius: '7px', height: 38, bgcolor: '#DC2626', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: '#B91C1C' } }}
                    >
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
